import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import en from './locales/en'
import es from './locales/es'

const LOCALES = { en, es }
// eslint-disable-next-line react-refresh/only-export-components -- i18n setup lives in one file
export const AVAILABLE_LANGUAGES = [
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
]

const DEFAULT_LANG = 'es'
const STORAGE_KEY = 'crowds_admin_lang'

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
})

function lookup(obj, path) {
  return path.split('.').reduce((acc, part) => (acc && acc[part] != null ? acc[part] : undefined), obj)
}

function interpolate(template, vars) {
  if (!vars || typeof template !== 'string') return template
  return template.replace(/\{(\w+)\}/g, (_, name) =>
    vars[name] !== undefined && vars[name] !== null ? String(vars[name]) : `{${name}}`
  )
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored && LOCALES[stored]) return stored
    } catch { /* ignore */ }
    return DEFAULT_LANG
  })

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang) } catch { /* ignore */ }
  }, [lang])

  const setLang = useCallback((next) => {
    if (LOCALES[next]) setLangState(next)
  }, [])

  const t = useCallback((key, vars) => {
    const current = LOCALES[lang] || LOCALES[DEFAULT_LANG]
    let value = lookup(current, key)
    if (value === undefined && lang !== DEFAULT_LANG) {
      value = lookup(LOCALES[DEFAULT_LANG], key)
    }
    if (value === undefined) return key
    return interpolate(value, vars)
  }, [lang])

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs beside its provider
export function useTranslation() {
  return useContext(I18nContext)
}
